import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from './Button';
import { Icon } from './icon';

/**
 * Modal de SUSPENSÃO com motivo obrigatório (V64).
 *
 * A V64 tornou `reasonCode` obrigatório nos endpoints de suspensão — sem coletar, o botão devolve
 * 400. O motivo é um código fechado (permite relatório agregado, "quantos por dívida") mais uma
 * observação livre; texto puro nunca daria o agregado.
 *
 * Reativar não passa por aqui: só suspender exige justificativa.
 */
export const SUSPEND_REASONS = [
  { code: 'FRAUDE_SUSPEITA', label: 'Suspeita de fraude' },
  { code: 'DOCUMENTO_INVALIDO', label: 'Documento inválido' },
  { code: 'DADOS_INCORRETOS', label: 'Dados incorretos' },
  { code: 'DEBITO_VENCIDO', label: 'Débito vencido' },
  { code: 'SOLICITACAO_DO_TITULAR', label: 'Solicitação do titular' },
  { code: 'OUTRO', label: 'Outro' },
] as const;

export function SuspendModal({
  open,
  name,
  loading = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  name?: string | undefined;
  loading?: boolean | undefined;
  onConfirm: (reasonCode: string, reasonNote: string) => void;
  onClose: () => void;
}): JSX.Element {
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setReasonCode('');
      setNote('');
    }
  }, [open, name]);

  const confirmDisabled = loading || reasonCode === '';

  const handleOpenChange = (next: boolean): void => {
    if (loading && !next) return;
    if (!next) onClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="merchant-theme fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-fade-in" />
        <Dialog.Content
          aria-describedby={undefined}
          className="merchant-theme fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[var(--m-r-lg)] bg-[var(--m-surface)] p-6 text-[var(--m-fg)] shadow-[var(--m-shadow-lg)] focus:outline-none data-[state=open]:animate-slide-up"
        >
          <div className="mb-4 flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--m-r-md)] bg-[var(--m-bg-alt)] text-[var(--m-brand-press)]">
              <Icon name="ban" size={20} />
            </span>
            <div className="min-w-0">
              <Dialog.Title className="text-[18px] font-extrabold tracking-[-0.01em] text-[var(--m-fg)]">
                Suspender
              </Dialog.Title>
              {name ? (
                <p className="mt-0.5 truncate text-[13px] text-[var(--m-fg-muted)]">{name}</p>
              ) : null}
            </div>
          </div>

          <label
            htmlFor="suspend-reason-code"
            className="mb-1.5 block text-[12px] font-bold uppercase tracking-[0.04em] text-[var(--m-fg-muted)]"
          >
            Motivo
          </label>
          <select
            id="suspend-reason-code"
            className="mb-4 h-11 w-full rounded-[var(--m-r-md)] border-[1.5px] border-[var(--m-border)] bg-[var(--m-surface)] px-3.5 text-[14px] text-[var(--m-fg)] outline-none focus:border-[var(--m-brand)]"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            disabled={loading}
          >
            <option value="">Selecione o motivo</option>
            {SUSPEND_REASONS.map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
          </select>

          <label
            htmlFor="suspend-reason-note"
            className="mb-1.5 block text-[12px] font-bold uppercase tracking-[0.04em] text-[var(--m-fg-muted)]"
          >
            Observação <span className="ml-1 normal-case text-[var(--m-fg-subtle)]">(opcional)</span>
          </label>
          <textarea
            id="suspend-reason-note"
            className="min-h-[80px] w-full resize-y rounded-[var(--m-r-md)] border-[1.5px] border-[var(--m-border)] bg-[var(--m-surface)] px-3.5 py-3 text-[14px] text-[var(--m-fg)] outline-none placeholder:text-[var(--m-fg-subtle)] focus:border-[var(--m-brand)]"
            placeholder="Detalhe o que motivou a suspensão."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={loading}
          />

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => onConfirm(reasonCode, note.trim())}
              disabled={confirmDisabled}
              aria-busy={loading || undefined}
            >
              Suspender
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
